import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { LuDog } from "react-icons/lu";
import { useForm } from "react-hook-form";
import { toNumber } from "lodash";
import { toast } from "react-hot-toast";
import InputWithStartAndEndAddOn from "@/app/components/ui/Inputs/InputWithStartAndEndAddOn";
import SearchableCouponSelectMenu from "@/app/components/ui/SelectMenus/SearchableCouponSelectMenu";
import CircleSpinnerLoader from "@/app/components/ui/Reusable/LoaderComponent/CircleSpinnerLoader";
import AlertBlue from "@/app/components/ui/Alerts/Blue";
import { useUrgentCancelStartAPI } from "@/app/hooks/api_hooks/Cart/useUrgentCancelStartAPI";
import { useUrgentCancelDraftCartAPI } from "@/app/hooks/api_hooks/Cart/useUrgentCancelDraftCartAPI";
import { useUrgentCancelEditAPI } from "@/app/hooks/api_hooks/Cart/useUrgentCancelEditAPI";
import { useUrgentCancelCheckoutAPI } from "@/app/hooks/api_hooks/Cart/useUrgentCancelCheckoutAPI";
import { useUrgentCancelAbortAPI } from "@/app/hooks/api_hooks/Cart/useUrgentCancelAbortAPI";
import {
  IUrgentCancelDraftCart,
  IUrgentCancelDraftCartCoupon,
  IUrgentCancelEditPayload,
  IUrgentCancelSession,
} from "@/api_framework/api_modals/Cart";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { useRouter } from "@/app/hooks/useRouter";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";

type Props = {
  open: boolean;
  setOpen: (value: boolean) => void;
  invoiceId: string;
  /** Human-readable invoice number; the id is only a fallback. */
  invoiceNumber?: string;
  /**
   * Clinic that raised the invoice. The session is opened against it, which is
   * not necessarily the clinic the user is logged into.
   */
  clinicId?: string;
  clinicName?: string;
  source: string;
};

type IUrgentCancelRow = {
  petId: string;
  uniqueRowId: string;
  name: string;
  tag: string;
  frozen: boolean;
  orderedQty: number;
  qty: number;
  originalNetPrice: number;
  netPrice: number;
  discount: number;
  couponId: string;
  coupons: IUrgentCancelDraftCartCoupon[];
};

type IUrgentCancelPetGroup = {
  petId: string;
  petName: string;
  rows: IUrgentCancelRow[];
};

// The draft cart opens as a clone of the invoice, so every row starts at its
// ordered qty and the stepper trims it down; `new_qty` is sent as-is. Prices are
// recomputed backend-side after each edit and are never derived here.
const groupDraftCart = (draftCart: IUrgentCancelDraftCart | null): IUrgentCancelPetGroup[] =>
  (draftCart?.pets ?? []).map((pet) => ({
    petId: pet?.pet_id,
    petName: pet?.pet_details?.name ?? "",
    rows: (pet?.items ?? []).map((item) => ({
      petId: pet?.pet_id,
      uniqueRowId: item?.unique_row_id,
      name: item?.name,
      tag: item?.tag ?? "",
      frozen: Boolean(item?.frozen),
      orderedQty: Number(item?.original_qty) || 0,
      qty: Number(item?.current_qty) || 0,
      originalNetPrice: Number(item?.original_net_price) || 0,
      netPrice: Number(item?.current_net_price) || 0,
      discount: Number(item?.current_discount) || 0,
      couponId: item?.coupon_id ?? "",
      coupons: item?.all_applicable_coupons ?? [],
    })),
  }));

const formatAmount = (value: number | undefined): string => (Number(value) || 0).toFixed(2);

// A coupon typed or pasted into the row picker is matched against the row's
// applicable list by name, so both sides are compared on the same footing: a
// pasted code carries stray whitespace and rarely matches the stored casing.
const normalizeCouponName = (value: string | undefined): string =>
  (value ?? "").replace(/\s+/g, " ").trim().toLowerCase();

const UrgentCancelItemsModal: React.FC<Props> = ({ open, setOpen, invoiceId, invoiceNumber, clinicId, clinicName }) => {
  const cancelButtonRef = useRef(null);
  // Kept in a ref so the unmount cleanup can still reach the live session.
  const sessionRef = useRef<IUrgentCancelSession | null>(null);

  const { getRouteKey } = useRouter();
  const { mappedClinicId, mappedClinicName } = useAppSelector((state) => state.root.UIStates);
  // The invoice is cancelled at the clinic that raised it, so no clinic switch is
  // asked for; the logged-in clinic only covers callers that send neither.
  const sessionClinicId = clinicId || mappedClinicId;
  const sessionClinicName = clinicName || mappedClinicName;
  const { customerId } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id")).routeState.state as ICustomerProfileState;

  const { execute: startUrgentCancel } = useUrgentCancelStartAPI();
  const { execute: fetchDraftCart } = useUrgentCancelDraftCartAPI();
  const { execute: editDraftCartItem } = useUrgentCancelEditAPI();
  const { execute: checkoutUrgentCancel } = useUrgentCancelCheckoutAPI();
  const { execute: abortUrgentCancel } = useUrgentCancelAbortAPI();

  const [draftCart, setDraftCart] = useState<IUrgentCancelDraftCart | null>(null);
  const [petGroups, setPetGroups] = useState<IUrgentCancelPetGroup[]>([]);
  // In-flight text for the typed fields, so a half-typed value never fires an
  // edit. Committed on blur / Enter and dropped whenever the cart is re-read.
  const [qtyDrafts, setQtyDrafts] = useState<Record<string, string>>({});

  // Backs the coupon search inputs only; the applied coupon lives on the row.
  const couponSearchForm = useForm<any>({ mode: "onChange", defaultValues: {} });

  const [loader, setLoader] = useState<boolean>(false);
  const [syncingRowId, setSyncingRowId] = useState<string | null>(null);
  const [hasEdited, setHasEdited] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isAborting, setIsAborting] = useState<boolean>(false);
  // Set when `start` is rejected because a session is already open for this
  // invoice — typically one stranded by a closed tab.
  const [conflict, setConflict] = useState<string | null>(null);
  // StrictMode mounts effects twice in dev. Without this the modal would open
  // two sessions and immediately abort one of them.
  const hasStartedRef = useRef(false);

  const store_pet_id = localStorage.getItem("selectedPetId");

  const applyDraftCart = (cart: IUrgentCancelDraftCart | null) => {
    setDraftCart(cart);
    setPetGroups(groupDraftCart(cart));
    setQtyDrafts({});
  };

  // Open a session, then read back the draft cart it created. Also the retry
  // path once a stranded session has been cleared.
  const startSession = async () => {
    setLoader(true);
    setConflict(null);
    const {
      status,
      session,
      conflict: isConflict,
      message,
    } = await startUrgentCancel({
      invoice_id: invoiceId,
      clinic_id: sessionClinicId,
      clinic_name: sessionClinicName,
    });

    if (!status || !session?.session_id) {
      if (isConflict) {
        setConflict(message || "An urgent cancel session is already open for this invoice.");
      }
      setLoader(false);
      return;
    }

    sessionRef.current = session;

    const { draftCart: cart } = await fetchDraftCart(session.session_id);
    applyDraftCart(cart);
    setLoader(false);
  };

  useEffect(() => {
    if (!open || !invoiceId || hasStartedRef.current) return;
    hasStartedRef.current = true;
    startSession();
  }, [open, invoiceId]);

  // Safety net for unmounts that skip the dismiss handler (route change, etc).
  useEffect(
    () => () => {
      if (sessionRef.current?.session_id) {
        abortUrgentCancel({ session_id: sessionRef.current.session_id });
        sessionRef.current = null;
      }
    },
    [],
  );

  const resetModalState = () => {
    applyDraftCart(null);
    setHasEdited(false);
    hasStartedRef.current = false;
  };

  // Leaving without checking out always releases the session.
  const dismissModal = async () => {
    if (isSubmitting || isAborting) return;
    const activeSessionId = sessionRef.current?.session_id;

    if (activeSessionId) {
      setIsAborting(true);
      await abortUrgentCancel({ session_id: activeSessionId });
      setIsAborting(false);
    }

    sessionRef.current = null;
    resetModalState();
    setConflict(null);
    setOpen(false);
  };

  // Every edit is applied against the draft cart straight away, then the cart is
  // re-read: coupons and discounts redistribute backend-side, so the totals and
  // the refund preview are only trustworthy coming from the server.
  const applyEdit = async (row: IUrgentCancelRow, changes: Partial<IUrgentCancelEditPayload>) => {
    const draftCartId = draftCart?.draft_cart_id ?? sessionRef.current?.draft_cart_id;
    const activeSessionId = sessionRef.current?.session_id;
    if (!draftCartId || !activeSessionId || row.frozen || syncingRowId) return;

    setSyncingRowId(row.uniqueRowId);
    const { status } = await editDraftCartItem({
      draft_cart_id: draftCartId,
      pet_id: row.petId,
      unique_row_id: row.uniqueRowId,
      new_qty: row.qty,
      ...changes,
    });
    if (status) {
      setHasEdited(true);
      const { draftCart: cart } = await fetchDraftCart(activeSessionId);
      applyDraftCart(cart);
    }
    setSyncingRowId(null);
  };

  const clearDraft = (setDrafts: React.Dispatch<React.SetStateAction<Record<string, string>>>, uniqueRowId: string) => {
    setDrafts((drafts) => {
      const rest = { ...drafts };
      delete rest[uniqueRowId];
      return rest;
    });
  };

  const commitQty = (row: IUrgentCancelRow, nextQty: number) => {
    const safeQty = Number.isFinite(nextQty) ? nextQty : row.qty;
    const clamped = Math.min(Math.max(Math.trunc(safeQty), 0), row.orderedQty);
    if (clamped === row.qty) {
      clearDraft(setQtyDrafts, row.uniqueRowId);
      return;
    }
    applyEdit(row, { new_qty: clamped });
  };

  // A blank field is an unfinished edit, not a request to zero the row out, so
  // it reverts to the server value. Cancelling a line means typing 0 explicitly.
  const commitQtyFromDraft = (row: IUrgentCancelRow) => {
    const draft = qtyDrafts[row.uniqueRowId];
    if (draft === undefined || draft.trim() === "") {
      clearDraft(setQtyDrafts, row.uniqueRowId);
      return;
    }
    commitQty(row, toNumber(draft.trim()));
  };

  // Edits are already live on the draft cart, so checkout only has to commit it.
  const onCheckout = async () => {
    const session = sessionRef.current;
    if (!session?.session_id || isSubmitting || syncingRowId) return;

    setIsSubmitting(true);
    const { status, cartId } = await checkoutUrgentCancel({ session_id: session.session_id });
    if (!status) {
      setIsSubmitting(false);
      return;
    }

    // Checkout owns the session now, so skip the abort on the way out.
    sessionRef.current = null;
    resetModalState();
    setIsSubmitting(false);
    setOpen(false);

    if (cartId) {
      window.open(
        `/app/user-cart/cart-creation/${customerId}/${cartId}?from_cart=true&&pet_id=${store_pet_id}`,
        "_self",
      );
    } else {
      window.location.reload();
    }
  };

  const allRows = petGroups.flatMap((group) => group.rows);
  const retainedItemCount = allRows.filter((row) => row.qty > 0).length;
  const totalDiscount = allRows.reduce((acc, row) => acc + row.discount, 0);

  // Prefer the backend breakup when it is sent; otherwise derive what the draft
  // cart payload allows. Gross is net plus the discount taken off it.
  const priceBreakup = draftCart?.price_breakup;
  const netTotal = Number(priceBreakup?.net_total_after_tax ?? draftCart?.current_total) || 0;
  const discountTotal = Number(priceBreakup?.total_discount ?? totalDiscount) || 0;
  const taxTotal = Number(priceBreakup?.total_tax) || 0;
  const grossTotal = Number(priceBreakup?.total_gross_price ?? netTotal + discountTotal) || 0;
  const itemsLabel = priceBreakup?.number_of_items ?? `${retainedItemCount} Items`;

  const isBusy = Boolean(syncingRowId) || isSubmitting || isAborting || loader;
  const isCheckoutDisabled = !hasEdited || isBusy;

  return (
    <div>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          initialFocus={cancelButtonRef}
          // Dismissing releases the live session, so it has to be deliberate:
          // the X and the footer buttons only, never a stray backdrop click.
          onClose={() => {}}
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
            </Transition.Child>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>

            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="inline-block w-full align-bottom bg-[#f7f8fa] rounded-xl text-left overflow-visible shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-[min(92vw,1440px)]">
                {/* Header */}
                <div className="flex items-center justify-between bg-white px-5 py-3 border-b rounded-t-xl">
                  <div>
                    <h2 className="text-sm font-semibold text-[#F72F03]">Urgent Cancel</h2>
                    <p className="text-xs text-[#808DA0] pt-0.5">{invoiceNumber || invoiceId}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {syncingRowId && <span className="text-xs text-[#808DA0]">Updating cart...</span>}
                    <XMarkIcon
                      className={`h-5 w-5 text-gray-500 ${isAborting ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                      onClick={dismissModal}
                    />
                  </div>
                </div>

                {conflict ? (
                  <div className="p-6">
                    <div className="bg-white rounded-xl shadow-md p-6 max-w-xl mx-auto text-center">
                      <h3 className="text-sm font-semibold text-[#F72F03]">{conflict}</h3>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col xl:flex-row gap-4 p-4">
                    {/* Items */}
                    <div className="flex-1 min-w-0">
                      <div className="bg-white rounded-xl shadow-md py-2">
                        {loader && petGroups.length === 0 ? (
                          <div className="flex justify-center py-16">
                            <CircleSpinnerLoader h="28" w="28" />
                          </div>
                        ) : petGroups.length === 0 ? (
                          <div className="p-3">
                            <AlertBlue title="No items found on this invoice." />
                          </div>
                        ) : (
                          petGroups.map((group) => (
                            <div key={group.petId} className="mb-2">
                              <div className="flex items-center pb-3 pt-2">
                                <div className="rounded-l-lg pl-2">
                                  <LuDog className="w-5 h-5" color="#f1380a" />
                                </div>
                                <p className="px-2 font-medium text-xs text-gray-500">
                                  <span className="text-black font-semibold text-sm">{group.petName}</span>
                                </p>
                                <div className="bg-[#e4e4e4] text-[#5b606d] py-1 px-3 rounded-md text-xs ml-3">
                                  {group.rows.length}
                                </div>
                              </div>
                              <table className="bg-white w-full table-fixed">
                                <thead>
                                  <tr className="text-left text-xs font-medium top-0 sticky z-10">
                                    <th className="sticky top-0 font-medium text-black text-xs px-4 py-2 z-20 bg-[#f5f5f5] rounded-tl-lg">
                                      Services &amp; Goods
                                    </th>
                                    <th className="sticky top-0 font-medium text-black text-xs px-4 py-2 z-20 bg-[#f5f5f5] w-20 whitespace-nowrap">
                                      Qty
                                    </th>
                                    <th className="sticky top-0 font-medium text-black text-xs px-4 py-2 z-20 bg-[#f5f5f5] whitespace-nowrap w-36">
                                      Quantity
                                    </th>
                                    <th className="sticky top-0 font-medium text-black text-xs px-4 py-2 z-20 bg-[#f5f5f5] whitespace-nowrap w-56">
                                      Coupon
                                    </th>
                                    <th className="sticky top-0 font-medium text-black text-xs px-4 py-2 z-20 bg-[#f5f5f5] whitespace-nowrap w-24">
                                      Discount
                                    </th>
                                    <th className="sticky top-0 font-medium text-black text-xs px-4 py-2 z-20 bg-[#f5f5f5] whitespace-nowrap w-24 rounded-tr-lg">
                                      Net
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {group.rows.map((row) => {
                                    const qtyDraft = qtyDrafts[row.uniqueRowId];
                                    const couponItems = row.coupons.map((coupon) => ({
                                      title: coupon?.coupon_name,
                                      id: coupon?.coupon_id,
                                      state: { coupon_visibility: coupon?.coupon_visibility },
                                    }));
                                    const isRowLocked = row.frozen || isBusy;
                                    // Nothing left on the cart to price, discount
                                    // or apply a coupon to.
                                    const isRowCancelled = row.qty === 0;

                                    return (
                                      <tr key={row.uniqueRowId} className={isRowCancelled ? "bg-[#fff5f4]" : ""}>
                                        <td className="p-4 text-black font-light text-xs break-words align-middle">
                                          <div className="font-medium">{row.name}</div>
                                          {row.tag && (
                                            <div className="text-[#8B909A] text-[10px] mt-0.5">{row.tag}</div>
                                          )}
                                          {row.frozen && (
                                            <div className="text-[#CB9A07] text-[10px] mt-0.5">Non-cancellable</div>
                                          )}
                                          {isRowCancelled && (
                                            <div className="text-[#F72F03] text-[10px] font-medium mt-0.5">
                                              Cancelling full item
                                            </div>
                                          )}
                                        </td>
                                        <td className="p-4 text-black font-light text-xs align-middle">
                                          {row.orderedQty}
                                        </td>
                                        <td className="p-4 text-black font-light text-xs align-middle whitespace-nowrap">
                                          {row.frozen ? (
                                            <span>{row.qty}</span>
                                          ) : (
                                            <InputWithStartAndEndAddOn
                                              inputClassName="min-w-[40px]"
                                              addonWrapperClassName="!mt-0"
                                              startAddon="-"
                                              startAddOnCallback={() => commitQty(row, row.qty - 1)}
                                              step={1}
                                              endAddon="+"
                                              endAddOnCallback={() => commitQty(row, row.qty + 1)}
                                              disabled={isRowLocked}
                                              value={qtyDraft ?? String(row.qty)}
                                              onChange={(e: any) =>
                                                setQtyDrafts((drafts) => ({
                                                  ...drafts,
                                                  [row.uniqueRowId]: e.target.value,
                                                }))
                                              }
                                              onBlur={() => commitQtyFromDraft(row)}
                                              onKeyDown={(e: any) => {
                                                if (e.key === "Enter") {
                                                  e.preventDefault();
                                                  e.currentTarget.blur();
                                                }
                                              }}
                                              type="number"
                                            />
                                          )}
                                        </td>
                                        <td className="p-4 text-black font-light text-xs break-words align-middle">
                                          {couponItems.length === 0 ? (
                                            <span className="text-[#8B909A]">NA</span>
                                          ) : (
                                            <SearchableCouponSelectMenu
                                              errorMessages={[]}
                                              fieldError={undefined}
                                              register={couponSearchForm.register(`coupon_search.${row.uniqueRowId}`)}
                                              onSelectItem={(item) => {
                                                const couponId = item?.id || "";
                                                if (couponId === row.couponId) return;
                                                applyEdit(row, { coupon_id: couponId });
                                              }}
                                              filterOnEmptyQuery={(item) => item.state?.coupon_visibility !== false}
                                              // Typing or pasting a coupon name and
                                              // pressing Enter applies it, so codes
                                              // kept out of the dropdown
                                              // (coupon_visibility: false) can still
                                              // be used — same as the cart row.
                                              onEnterPress={(typedValue) => {
                                                const typedCoupon = normalizeCouponName(typedValue);
                                                if (!typedCoupon) return;
                                                const exactMatch = row.coupons?.find(
                                                  (c) => normalizeCouponName(c?.coupon_name) === typedCoupon,
                                                );
                                                if (!exactMatch) {
                                                  toast.error("Coupon not applicable");
                                                  return;
                                                }
                                                if (exactMatch.coupon_id === row.couponId) return;
                                                applyEdit(row, { coupon_id: exactMatch.coupon_id });
                                              }}
                                              optionRenderVariant="DEFAULT"
                                              disabled={isRowLocked || isRowCancelled}
                                              selectItems={couponItems}
                                              placeholder="Select Coupon"
                                              showTooltips={true}
                                              showTypedErrors={false}
                                              showDropdownIcon={true}
                                              withReset={true}
                                              defaultSelected={couponItems.find((oc) => oc.id === row.couponId)}
                                              listZIndex="z-[60]"
                                              listBoxClassName="w-[360px]"
                                              className="text-gray-800 w-10"
                                              containerClassName="w-full"
                                            />
                                          )}
                                        </td>
                                        <td className="p-4 text-black font-light text-xs align-middle">
                                          {formatAmount(row.discount)}
                                        </td>
                                        <td className="p-4 text-black font-light text-xs align-middle">
                                          <div>{formatAmount(row.netPrice)}</div>
                                          {row.netPrice !== row.originalNetPrice && (
                                            <div className="text-[#8B909A] line-through text-[10px]">
                                              {formatAmount(row.originalNetPrice)}
                                            </div>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Price breakup */}
                    <div className="w-full xl:w-80 shrink-0">
                      <div className="bg-white p-4 shadow-md rounded-xl">
                        <div className="flex gap-5">
                          <h1 className="text-base font-medium">Price Breakup</h1>
                          <div className="bg-[#e4e4e4] text-[#5b606d] py-1 px-3 rounded-md text-xs">{itemsLabel}</div>
                        </div>
                        <div className="flex justify-between mt-3">
                          <p className="text-[#6d717f] text-sm font-medium">Total MRP</p>
                          <p className="text-[#6d717f] text-sm">{formatAmount(grossTotal)}</p>
                        </div>
                        <div className="flex justify-between mt-3">
                          <p className="text-[#6d717f] text-sm font-medium">Discount</p>
                          <p className="text-[#6d717f] text-sm">{formatAmount(discountTotal)}</p>
                        </div>
                        <div className="flex justify-between mt-3">
                          <p className="text-[#6d717f] text-sm font-medium">Total Tax</p>
                          <p className="text-[#6d717f] text-sm">{formatAmount(taxTotal)}</p>
                        </div>
                        <div className="flex justify-between mt-3">
                          <p className="text-black text-sm font-medium">Net Total</p>
                          <p className="text-black text-sm">{formatAmount(netTotal)}</p>
                        </div>

                        {isBusy ? (
                          <div className="flex justify-center mt-4">
                            <CircleSpinnerLoader h="20" w="20" />
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={onCheckout}
                            disabled={isCheckoutDisabled}
                            className={`w-full px-10 py-[9px] text-sm mt-3 rounded-lg shadow-sm text-white font-medium
                            ${isCheckoutDisabled ? "bg-gray-300 cursor-not-allowed" : "bg-[#07863d] cursor-pointer"}`}
                          >
                            Checkout
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default UrgentCancelItemsModal;
